import React from 'react';
import { Layout } from '../../components/Layout';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Checklist } from '../../components/Checklist';
import { ORG_GOVERN_CHECKLIST } from '../../utils/constants';
import { Users, AlertTriangle, Zap, ArrowRight, ArrowLeft, Download } from 'lucide-react';
import { Link } from 'react-router-dom';
export function Govern() {
  const breadcrumbs = [{
    label: 'Protocols',
    path: '/'
  }, {
    label: 'Organization Protocol',
    path: '/organization'
  }, {
    label: 'Governance & Leadership'
  }];
  return <Layout breadcrumbs={breadcrumbs} data-id="element-3958">
      <div className="container mx-auto px-4 py-12" data-id="element-3959">
        {/* Header */}
        <div className="mb-12" data-id="element-3960">
          <div className="inline-flex items-center px-3 py-1 rounded-full bg-cyan-500/10 text-cyan-500 text-xs font-medium mb-4 border border-cyan-500/20" data-id="element-3961">
            <Users className="h-3 w-3 mr-2" data-id="element-3962" />
            Domain 1 of 6
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-slate-100 mb-4" data-id="element-3963">
            Governance & Leadership
          </h1>
          <p className="text-xl text-slate-400 max-w-3xl" data-id="element-3964">
            Establish clear security ownership, policies, and decision-making
            structures to guide your organization's security program.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-3965">
          <div className="lg:col-span-2 space-y-12" data-id="element-3966">
            {/* What This Domain Covers */}
            <section data-id="element-3967">
              <h2 className="text-2xl font-bold text-slate-100 mb-4" data-id="element-3968">
                What This Domain Covers
              </h2>
              <ul className="space-y-2 text-slate-300" data-id="element-3969">
                <li className="flex items-start" data-id="element-3970">
                  <span className="text-cyan-500 mr-2" data-id="element-3971">•</span>
                  Security roles and responsibilities (who owns what)
                </li>
                <li className="flex items-start" data-id="element-3972">
                  <span className="text-cyan-500 mr-2" data-id="element-3973">•</span>
                  Core security policies and standards
                </li>
                <li className="flex items-start" data-id="element-3974">
                  <span className="text-cyan-500 mr-2" data-id="element-3975">•</span>
                  Risk management and decision-making processes
                </li>
                <li className="flex items-start" data-id="element-3976">
                  <span className="text-cyan-500 mr-2" data-id="element-3977">•</span>
                  Third-party and vendor governance
                </li>
                <li className="flex items-start" data-id="element-3978">
                  <span className="text-cyan-500 mr-2" data-id="element-3979">•</span>
                  Security awareness and training programs
                </li>
              </ul>
            </section>

            {/* Minimum Baseline Controls */}
            <section data-id="element-3980">
              <h2 className="text-2xl font-bold text-slate-100 mb-4" data-id="element-3981">
                Minimum Baseline Controls
              </h2>
              <div className="space-y-3" data-id="element-3982">
                <ControlItem title="Appoint Security Owner" description="Designate one person responsible for security decisions (can be part-time for small orgs)." data-id="element-3983" />
                <ControlItem title="Document Acceptable Use Policy" description="Define what employees can and cannot do with company devices and accounts." data-id="element-3984" />
                <ControlItem title="Establish Password Policy" description="Require strong, unique passwords and MFA for all critical accounts." data-id="element-3985" />
                <ControlItem title="Define Data Classification" description="Label data as Public, Internal, or Confidential to guide handling." data-id="element-3986" />
                <ControlItem title="Create Vendor Approval Process" description="Require security review before using new cloud services or vendors." data-id="element-3987" />
                <ControlItem title="Conduct Annual Security Training" description="Train all employees on phishing, passwords, and data protection basics." data-id="element-3988" />
              </div>
            </section>

            {/* Best Practice Controls */}
            <section data-id="element-3989">
              <h2 className="text-2xl font-bold text-slate-100 mb-4" data-id="element-3990">
                Best Practice Controls
              </h2>
              <div className="space-y-3" data-id="element-3991">
                <ControlItem title="Establish Security Committee" description="Form a cross-functional team (IT, Legal, HR, Operations) to oversee security." data-id="element-3992" />
                <ControlItem title="Create Risk Register" description="Document top risks, their likelihood, impact, and mitigation plans." data-id="element-3993" />
                <ControlItem title="Define Risk Appetite" description="Document what level of risk leadership is willing to accept." data-id="element-3994" />
                <ControlItem title="Implement Vendor Risk Management" description="Assess vendor security before contract and annually thereafter." data-id="element-3995" />
                <ControlItem title="Establish Security Metrics" description="Track key indicators like MFA adoption, patching compliance, training completion." data-id="element-3996" />
                <ControlItem title="Conduct Quarterly Reviews" description="Review security posture, incidents, and improvements with leadership." data-id="element-3997" />
                <ControlItem title="Document Security Roles (RACI)" description="Define who is Responsible, Accountable, Consulted, and Informed for each security task." data-id="element-3998" />
              </div>
            </section>

            {/* Common Mistakes */}
            <section data-id="element-3999">
              <h2 className="text-2xl font-bold text-slate-100 mb-4 flex items-center" data-id="element-4000">
                <AlertTriangle className="h-6 w-6 text-red-500 mr-3" data-id="element-4001" />
                Common Mistakes
              </h2>
              <div className="space-y-3 text-slate-400" data-id="element-4002">
                <p className="flex items-start" data-id="element-4003">
                  <span className="text-red-500 mr-2" data-id="element-4004">✗</span>
                  Assuming IT owns all security (security is everyone's
                  responsibility)
                </p>
                <p className="flex items-start" data-id="element-4005">
                  <span className="text-red-500 mr-2" data-id="element-4006">✗</span>
                  Creating policies that no one reads or follows (keep them
                  short and practical)
                </p>
                <p className="flex items-start" data-id="element-4007">
                  <span className="text-red-500 mr-2" data-id="element-4008">✗</span>
                  Skipping vendor security reviews to save time (supply chain
                  attacks are common)
                </p>
                <p className="flex items-start" data-id="element-4009">
                  <span className="text-red-500 mr-2" data-id="element-4010">✗</span>
                  One-time training only (security awareness needs regular
                  reinforcement)
                </p>
              </div>
            </section>

            {/* Quick Wins */}
            <section data-id="element-4011">
              <h2 className="text-2xl font-bold text-slate-100 mb-4 flex items-center" data-id="element-4012">
                <Zap className="h-6 w-6 text-yellow-500 mr-3" data-id="element-4013" />
                Quick Wins (7 Days)
              </h2>
              <div className="space-y-3" data-id="element-4014">
                <QuickWin day="Day 1-2" action="Appoint security owner and announce to team" data-id="element-4015" />
                <QuickWin day="Day 3-4" action="Draft 1-page acceptable use policy (use template)" data-id="element-4016" />
                <QuickWin day="Day 5-6" action="Create list of all cloud services and vendors in use" data-id="element-4017" />
                <QuickWin day="Day 7" action="Schedule first security awareness session (phishing basics)" data-id="element-4018" />
              </div>
            </section>
          </div>

          {/* Sidebar */}
          <div className="space-y-6" data-id="element-4019">
            <Checklist id="org-governance" title="Implementation Checklist" items={ORG_GOVERN_CHECKLIST} data-id="element-4020" />

            <Card className="p-6 bg-slate-900/50 border-slate-800" data-id="element-4021">
              <h3 className="font-bold text-slate-100 mb-4" data-id="element-4022">
                Download Templates
              </h3>
              <Link to="/downloads/policies" data-id="element-4023">
                <Button variant="secondary" fullWidth className="mb-3" data-id="element-4024">
                  <Download className="h-4 w-4 mr-2" data-id="element-4025" />
                  Policy Templates
                </Button>
              </Link>
              <Link to="/downloads/organization-pack" data-id="element-4026">
                <Button variant="secondary" fullWidth data-id="element-4027">
                  <Download className="h-4 w-4 mr-2" data-id="element-4028" />
                  Governance Pack
                </Button>
              </Link>
            </Card>

            <Card className="p-6 bg-slate-900/50 border-slate-800" data-id="element-4029">
              <h3 className="font-bold text-slate-100 mb-4" data-id="element-4030">Next Steps</h3>
              <p className="text-sm text-slate-400 mb-4" data-id="element-4031">
                Once governance is established, move to identifying your assets
                and risks.
              </p>
              <Link to="/organization/identify" data-id="element-4032">
                <Button variant="outline" fullWidth data-id="element-4033">
                  Next: Assets & Risk Awareness
                  <ArrowRight className="h-4 w-4 ml-2" data-id="element-4034" />
                </Button>
              </Link>
            </Card>
          </div>
        </div>

        {/* Navigation */}
        <div className="mt-12 flex justify-between items-center border-t border-slate-800 pt-8" data-id="element-4035">
          <Link to="/organization" data-id="element-4036">
            <Button variant="outline" data-id="element-4037">
              <ArrowLeft className="h-4 w-4 mr-2" data-id="element-4038" />
              Back to Overview
            </Button>
          </Link>
          <Link to="/organization/identify" data-id="element-4039">
            <Button data-id="element-4040">
              Next Domain
              <ArrowRight className="h-4 w-4 ml-2" data-id="element-4041" />
            </Button>
          </Link>
        </div>
      </div>
    </Layout>;
}
function ControlItem({
  title,
  description
}: {
  title: string;
  description: string;
}) {
  return <Card className="p-4 bg-slate-900/50 border-slate-800" data-id="element-4042">
      <h4 className="font-bold text-slate-100 text-sm mb-1" data-id="element-4043">{title}</h4>
      <p className="text-slate-400 text-sm" data-id="element-4044">{description}</p>
    </Card>;
}
function QuickWin({
  day,
  action
}: {
  day: string;
  action: string;
}) {
  return <div className="flex items-start" data-id="element-4045">
      <span className="text-yellow-500 font-mono text-sm mr-3 mt-0.5" data-id="element-4046">
        {day}
      </span>
      <span className="text-slate-300 text-sm" data-id="element-4047">{action}</span>
    </div>;
}